import React, { useEffect, useCallback, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { trackShareLanding } from './services/growthService';
import { GameProvider, useGame, useWebSocket, WebSocketProvider } from './context/GameContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { AccessibilityProvider } from './hooks/useAccessibility';
import { websocketService } from './services/websocketService';

// Critical-path screens (eager) — splash, auth, home, gameplay, lobby,
// solo setup, profile setup. Anything you might need within seconds of
// landing.
import SplashScreen from './screens/SplashScreen';
import LandingScreen from './screens/LandingScreen';
import HomeScreen from './screens/HomeScreen';
import LoginScreen from './screens/LoginScreen';
import GameScreen from './screens/GameScreen/GameScreen';
import GameResultsScreen from './screens/GameResultsScreen';
import LobbyScreen from './screens/LobbyScreen';
import SoloSetupScreen from './screens/SoloSetupScreen';
import ProfileScreen from './screens/ProfileScreen';

// Non-critical screens — lazy-loaded. Cuts the initial JS bundle from
// ~1.8MB to ~600KB so first paint is dramatically snappier.
const SignUpScreen = lazy(() => import('./screens/SignUpScreen'));
const ForgotPasswordScreen = lazy(() => import('./screens/ForgotPasswordScreen'));
const ResultsScreen = lazy(() => import('./screens/ResultsScreen'));
const PublicGameResultsScreen = lazy(() => import('./screens/PublicGameResultsScreen'));
const FriendsScreen = lazy(() => import('./screens/FriendsScreen'));
const FriendProfileScreen = lazy(() => import('./screens/FriendProfileScreen'));
const LeaderboardScreen = lazy(() => import('./screens/LeaderboardScreen'));
const PublicProfileScreen = lazy(() => import('./screens/PublicProfileScreen'));
const SettingsScreen = lazy(() => import('./screens/SettingsScreen'));
const BlockedPlayersScreen = lazy(() => import('./screens/BlockedPlayersScreen'));
const PacksScreen = lazy(() => import('./screens/PacksScreen'));
/* Admin-only, and the server enforces it: GET /api/metrics/conversions is gated
   on an ADMIN_UIDS allowlist and FAILS CLOSED (unset ⇒ 403 to everybody). The
   route being reachable in the client grants nothing. */
const AdminGrowthScreen = lazy(() => import('./screens/AdminGrowthScreen'));
const DebugScreen = lazy(() => import('./screens/DebugScreen'));
const EdgeStateScreen = lazy(() => import('./screens/EdgeStateScreen'));
const StatsScreen = lazy(() => import('./screens/StatsScreen'));
const GameHistoryScreen = lazy(() => import('./screens/GameHistoryScreen'));
const AchievementsScreen = lazy(() => import('./screens/AchievementsScreen'));
const PlansScreen = lazy(() => import('./screens/PlansScreen'));
const HowItWorksScreen = lazy(() => import('./screens/HowItWorksScreen'));
const DailyChallengeScreen = lazy(() => import('./screens/DailyChallengeScreen'));
const TVCanvasScreen = lazy(() => import('./screens/TVCanvasScreen'));
/* The Table — a roster and its memory. Reached from the Home tables row and
   from the "Keep this table?" card on results. There is no create route: a
   table is only ever kept from a game that already happened. */
const TablesScreen = lazy(() => import('./screens/TablesScreen'));
const TableScreen = lazy(() => import('./screens/TableScreen'));

import { AppNav, InGameNav, ActiveGamePill, IncomingInviteToast, SkeletonList, ProfilePreviewProvider, ToastProvider, ErrorBoundary, useToast } from './components/wr';

// Components
import ProtectedRoute from './components/ProtectedRoute/ProtectedRoute';
import LoadingSpinner from './components/LoadingSpinner';
import { UpdateNotification } from './components/UpdateNotification';

// Handle direct join links
function DirectJoinHandler() {
  const navigate = useNavigate();
  const { roomCode } = useParams();
  const { joinRoom } = useWebSocket();
  const { currentUser, signInAsGuest, loading: authLoading } = useAuth();
  const { state } = useGame();

  /*
   * They got here from a link somebody sent them. Stamp it, so that if they go
   * on to start a table of their own — tonight, or three Sundays from now — we
   * can tell that the loop closed. See growthService.markBecameHost.
   */
  useEffect(() => {
    if (roomCode) trackShareLanding('invite-link', roomCode);
  }, [roomCode]);

  useEffect(() => {
    const handleJoin = async () => {
      if (!roomCode) {
        navigate('/home');
        return;
      }
      // Auto-create a guest account so share links work without a sign-in
      // wall. The visitor lands directly in the lobby with an auto-name;
      // they can rename themselves later via Profile.
      if (!authLoading && !currentUser) {
        try {
          console.log('🔑 No account yet — auto-creating guest for share-link join');
          await signInAsGuest();
          // After sign-in completes, this effect will re-fire with
          // currentUser set, falling through to the join path below.
          return;
        } catch (e) {
          console.error('Guest sign-in failed:', e);
          navigate(`/login?redirect=${encodeURIComponent(`/join/${roomCode}`)}`);
          return;
        }
      }
      if (!currentUser) return; // still loading auth
      try {
        const normalized = roomCode.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
        console.log(`🔗 Direct join: Attempting to join room ${normalized}`);
        await joinRoom(normalized);
      } catch (error: any) {
        console.error('❌ Failed to join room:', error);
        const msg = (error?.message || '').toLowerCase();
        if (msg.includes('full')) {
          navigate('/error?type=full');
        } else if (msg.includes('not found') || msg.includes('timeout')) {
          navigate(`/error?type=not-found&detail=${encodeURIComponent(roomCode)}`);
        } else {
          navigate('/home');
        }
      }
    };
    handleJoin();
  }, [roomCode, currentUser, authLoading, joinRoom, navigate, signInAsGuest]);

  // The handleJoin path emits the joinRoom socket event; the server replies
  // with room_joined, which sets state.roomCode in GameContext. Watch for
  // that and navigate ourselves — the previous "navigation will be handled
  // by WebSocket events" comment was wishful thinking; nothing was actually
  // doing it, so users got stuck on the "Joining game..." spinner.
  useEffect(() => {
    if (state.roomCode && state.currentScreen !== 'home') {
      console.log('🚪 DirectJoinHandler: state.roomCode set, navigating to lobby:', state.roomCode);
      navigate(`/room/${state.roomCode}`, { replace: true });
    }
  }, [state.roomCode, state.currentScreen, navigate]);

  return (
    <div className="app-shell wr-center" style={{ minHeight: '100vh' }}>
      <div className="text-center">
        <LoadingSpinner size="lg" />
        <p className="mt-4 text-lg font-medium text-gray-700">Joining game...</p>
        <p className="text-sm text-gray-600">Room Code: {roomCode}</p>
      </div>
    </div>
  );
}

// Handle query parameter URLs and redirect to route parameter format
function LobbyRedirectHandler() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  useEffect(() => {
    const roomCode = searchParams.get('room');
    console.log('🔄 LobbyRedirectHandler: Query param room code:', roomCode);
    
    if (roomCode) {
      console.log('🔄 Redirecting from /lobby?room=' + roomCode + ' to /room/' + roomCode);
      navigate(`/room/${roomCode}`, { replace: true });
    } else {
      console.log('🔄 No room code in query params, redirecting to home');
      navigate('/home', { replace: true });
    }
  }, [searchParams, navigate]);

  return (
    <div className="app-shell wr-center" style={{ minHeight: '100vh' }}>
      <div className="text-center">
        <div className="text-4xl mb-4">🔄</div>
        <h2 className="text-xl font-semibold text-gray-800 mb-2">Redirecting...</h2>
        <p className="text-gray-600">Processing room link...</p>
      </div>
    </div>
  );
}

// Wrapper components that provide props to screens
/* PlayerSetupScreenWrapper REMOVED as its functionality is now in ProfileScreen and SplashScreen
function PlayerSetupScreenWrapper() {
 ...
}
*/

function HomeScreenWrapper() {
  const { state } = useGame();
  const navigate = useNavigate();

  // Auto-redirect to /setup is intentionally REMOVED. Anonymous and Google
  // sign-in both seed `displayName` at account creation (a generated
  // "Witty Fox" name for guests, the Google name otherwise), so a missing
  // displayName almost never reflects the user's intent. Worse, any legacy
  // backend write that uses `display_name` (snake_case) leaves the
  // camelCase field undefined and triggered an infinite "every refresh
  // sends me back to setup" loop. Home now renders unconditionally; users
  // who want to change their name navigate via the avatar → Profile.
  
  /**
   * If room code exists in state, navigate to lobby.
   *
   * EXCEPT FOR AN ASYNC GAME, WHICH MUST NOT DRAG YOU OFF THE HOME SCREEN.
   *
   * Holding a room code is a transient state in a live game — you just created or
   * joined one, and the lobby is where you are going. Holding one in a
   * correspondence game is a state you can be in for a DAY, and this fired on
   * every visit to Home: Mom opens the app to the one screen built to tell her
   * "Your turn", and is bounced to the lobby before she can read it (and from
   * there, straight into the game by the guard below).
   *
   * The net effect was that the home screen — the primary in-app notification
   * surface for this entire mode — was unreachable for precisely the people who
   * had a turn waiting. It is offered on Home and in the table thread; she goes
   * when she is ready.
   */
  const isAsyncGame = !!state.gameSettings?.isAsync;

  useEffect(() => {
    if (isAsyncGame) return;
    if (state.roomCode) {
      console.log('🚀 Room created/joined, navigating to lobby:', state.roomCode);
      navigate(`/room/${state.roomCode}`);
    }
  }, [state.roomCode, isAsyncGame, navigate]);
  
  if (!state.currentPlayer) {
    return (
      <div className="app-shell wr-center" style={{ minHeight: '100vh' }}>
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }
  
  return (
    <HomeScreen 
      currentPlayer={{
        id: state.currentPlayer.id,
        name: state.currentPlayer.name,
        avatar: state.currentPlayer.avatar
      }}
    />
  );
}

function LobbyScreenWrapper() {
  const { state, dispatch } = useGame();
  const navigate = useNavigate();
  const params = useParams();
  const roomCodeParam = params.roomCode;
  const { joinRoom, leaveRoom, setPlayerReady, startGame } = useWebSocket();
  
  // Use room code from URL params or game state
  const roomCode = roomCodeParam || state.roomCode;
  
  // Enhanced debugging and room state management
  useEffect(() => {
    console.log('🏠 LobbyScreenWrapper mounted');
    console.log('🔍 URL params:', params);
    console.log('🔍 URL room code:', roomCodeParam);
    console.log('🔍 State room code:', state.roomCode);
    console.log('🔍 Final room code:', roomCode);
    console.log('🔍 Current player:', state.currentPlayer?.name);
    console.log('🔍 Is host:', state.isHost);
    console.log('🔍 Players count:', state.players.length);
    
    // If no valid room code, redirect to home
    if (!roomCode) {
      console.log('❌ No valid room code - redirecting to home');
      navigate('/home');
      return;
    }
    
    // If we have a URL room code but no state room code, try to join
    if (roomCodeParam && !state.roomCode && state.currentPlayer && state.isConnected) {
      console.log('🎯 Attempting to join room from URL:', roomCodeParam);
      console.log('🔌 WebSocket connected:', state.isConnected);
      joinRoom(roomCodeParam).catch((error: any) => {
        console.error('❌ Failed to join room from URL:', error);
        // If join fails, redirect to home
        navigate('/home');
      });
    }
  }, [roomCodeParam, state.roomCode, state.currentPlayer, state.isConnected, joinRoom, navigate]);
  
  // Enhanced leave room function with navigation
  const handleLeaveRoom = async () => {
    try {
      console.log('🚪 Leaving room:', roomCode);
      await leaveRoom();
      dispatch({ type: 'LEAVE_ROOM' });
      navigate('/home');
    } catch (error) {
      console.error('Error leaving room:', error);
      // Force navigate even if there was an error
      navigate('/home');
    }
  };

  // Fix handleStartGame type issue
  const handleStartGame = useCallback(() => {
    console.log('🚀 Starting game from lobby');
    return startGame();
  }, [startGame]);

  // If no room code, show loading while redirecting
  if (!roomCode) {
    return (
      <div className="app-shell wr-center" style={{ minHeight: '100vh' }}>
        <div className="text-center">
          <div className="text-4xl mb-4">🔄</div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Checking Room Status</h2>
          <p className="text-gray-600">Redirecting to home...</p>
        </div>
      </div>
    );
  }
  
  // If we have a room code from URL but waiting for WebSocket connection
  if (roomCodeParam && !state.isConnected && !state.roomCode) {
    return (
      <div className="app-shell wr-center" style={{ minHeight: '100vh' }}>
        <div className="text-center">
          <div className="text-4xl mb-4">🔌</div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Connecting to Server</h2>
          <p className="text-gray-600">Preparing to join room {roomCodeParam}...</p>
        </div>
      </div>
    );
  }
  
  return (
    <LobbyScreen
      roomCode={roomCode}
      isHost={state.isHost}
      players={state.players}
      // GameContext's GameSettings has an 'async' mode that LobbyScreen's
      // local GameSettings type doesn't declare. The two types need to be
      // unified (LobbyScreen is another agent's file); cast so this file
      // typechecks in the meantime.
      gameSettings={state.gameSettings as any}
      onStartGame={handleStartGame}
      onLeaveRoom={handleLeaveRoom}
      onPlayerReady={setPlayerReady}
      onUpdateSettings={(settings) => {
        console.log('⚙️ Settings update:', settings);
        dispatch({ type: 'UPDATE_GAME_SETTINGS', payload: settings });
      }}
      currentPlayer={state.currentPlayer}
      isLoading={state.isLoading}
    />
  );
}

// App content with routing
function AppContent() {
  const { state, dispatch } = useGame();
  const { currentUser, userProfile, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  // A Cloud Run deploy SIGTERMs the server. It flushes every live room to
  // Firestore and the next revision rehydrates them, so the game genuinely
  // survives — but socket.io goes quiet for a few seconds while that happens.
  // Without this the screen just stops responding, which reads as a crash.
  useEffect(() => {
    const onRestarting = () => {
      toast.show("Updating — your game is saved. Reconnecting…", {
        kind: 'info',
        durationMs: 8000,
      });
    };
    websocketService.on('server_restarting', onRestarting);
    return () => websocketService.off('server_restarting', onRestarting);
  }, [toast]);

  /**
   * THE DOOR SAID NO — and now it says so out loud.
   *
   * The server has always refused a blocked join (`safety.findBlockConflict`,
   * symmetric, on every stranger-visible room). Nothing listened, so the refusal
   * was delivered as a ten-second hang followed by "Join room timeout" — the
   * one thing the mechanism could not afford to look like was a bug.
   *
   * Two distinct refusals, and they need different words. `sign-in-required` is
   * a door you can walk through (make an account); `blocked` is a door that is
   * shut, and we do NOT say by whom — telling someone which of the players in
   * there blocked them is how a block becomes a provocation.
   */
  useEffect(() => {
    const onJoinBlocked = (data: { code?: string; message?: string }) => {
      if (data?.code === 'sign-in-required') {
        toast.show(
          data.message || "Create an account to play with people you don't know.",
          { kind: 'info', durationMs: 6000, actionLabel: 'Sign in', onAction: () => navigate('/signup') },
        );
        return;
      }
      toast.error(data?.message || "You can't join this game.");
      navigate('/home');
    };
    websocketService.on('joinBlocked', onJoinBlocked);
    return () => websocketService.off('joinBlocked', onJoinBlocked);
  }, [toast, navigate]);

  // Firebase-based user initialization
  useEffect(() => {
    const firebaseAuthEnabled = import.meta.env.VITE_ENABLE_FIREBASE_AUTH === 'true';
    
    if (firebaseAuthEnabled && currentUser && userProfile) {
      console.log('🔥 Firebase authentication enabled - user authenticated:', currentUser.email);

      // Hydrate player from Firebase. Avatar precedence:
      // 1) explicit pick stored in userProfile.avatar (branded id like "fox"
      //    OR a photoURL the user opted into),
      // 2) Google account photo as a sane default (rendered as <img>),
      // 3) the branded "fox" id.
      // Migration: older profiles stored emoji ('👤', '🎮', '🐶'...) which
      // our redesigned Avatar component hashes into a random-looking branded
      // squircle. If the user actually has a Google photo, prefer that.
      const stored = (userProfile.avatar || '').trim();
      const looksLikeUrl = /^https?:\/\//i.test(stored);
      const looksLikeBrandId = /^[a-z]{3,12}$/.test(stored);
      const photo = (currentUser as any).photoURL;
      const avatar =
        (looksLikeUrl || looksLikeBrandId) ? stored :
        photo || stored || 'fox';
      const player = {
        id: currentUser.uid,
        name: userProfile.displayName || currentUser.displayName || 'Player',
        avatar,
        isReady: false,
        isHost: false,
        score: 0
      };
      dispatch({ type: 'SET_PLAYER', payload: player });
    } else if (!firebaseAuthEnabled) {
      console.log('⚠️ Firebase authentication disabled - using demo mode');
      // Only set demo user if Firebase is disabled
      const demoUser = {
        id: 'demo-user',
        name: 'Demo Player',
        avatar: '🎮',
        isReady: false,
        isHost: false,
        score: 0
      };
      dispatch({ type: 'SET_PLAYER', payload: demoUser });
    }
  }, [currentUser, userProfile, dispatch]);

  // External navigation channel — non-React code (websocket handlers,
  // global listeners) can fire `wr:navigate` events to ask App.tsx to
  // navigate. The kicked-from-room flow uses this since GameContext
  // event handlers don't have access to useNavigate.
  useEffect(() => {
    const onNav = (e: any) => {
      const to = e?.detail?.to;
      const replace = !!e?.detail?.replace;
      if (typeof to === 'string') navigate(to, { replace });
    };
    window.addEventListener('wr:navigate', onNav);
    return () => window.removeEventListener('wr:navigate', onNav);
  }, [navigate]);

  // CRITICAL FIX: Handle game state navigation. We watch state.currentScreen
  // because the GameContext's RESTORE_GAME_STATE reducer (post-refresh
  // rehydration) sets it to whatever phase the server says we're in. This
  // effect carries the user to the right route automatically.
  // BUT we only auto-navigate if the user isn't already on a sensible
  // route — without that guard, manually navigating to /home from inside a
  // game would bounce right back. The leave-game flow dispatches
  // SET_SCREEN: 'home' before navigating to break the cycle.
  useEffect(() => {
    const path = location.pathname;

    /**
     * THE TELEVISION IS NOT A PLAYER, AND THIS ROUTER MUST NOT MOVE IT.
     *
     * /tv/:code is an OBSERVER. It shares this app, this GameContext and this
     * socket — so when the game ended, `currentScreen` flipped to 'results' and
     * this effect dutifully dragged the family's television off the stage and
     * onto a personal results page, mid-cascade, in front of everybody.
     *
     * The TV owns its own phase machine, driven by the room's broadcasts. It
     * has no business being routed by a player's screen state.
     */
    if (path.startsWith('/tv')) return;

    /**
     * AN ASYNC GAME IS AN INVITATION, NOT A SUMMONS.
     *
     * This guard drags you back into a game in progress from wherever you are, and
     * for a live game that is exactly right: it lasts sixty seconds, three people
     * are waiting, and a stray tap on the wrong tab should not cost you the round.
     *
     * A correspondence game lasts a DAY. Applying the same rule to it builds a cage:
     * Mom, who has an open turn, can no longer visit her table, or the home screen,
     * or anything else — every navigation snaps her back to the sheet. And it is
     * worse AFTER she plays, because `currentScreen` is still 'game': she is pinned
     * to a "waiting for 1 other player" screen that is, at that very moment, telling
     * her it is safe to close the tab.
     *
     * So async games are OFFERED rather than IMPOSED. She finds hers on the home
     * screen ("Your turn", sorted to the top) and in the table thread — surfaces
     * built for precisely this — and she goes when she is ready.
     *
     * ONE EXCEPTION, and it is not an exception to the principle: coming out of
     * the LOBBY of this very room. That is not the app dragging you somewhere you
     * did not ask to go — it is the game starting under your feet, in the room you
     * are standing in, which is the entire job the lobby hands off. Suppressing it
     * there stranded the person who had just pressed the button on a settings sheet
     * for a round that had already begun without them.
     */
    const isAsyncGame = !!state.gameSettings?.isAsync;
    const leavingOwnLobby = path.startsWith('/room/');

    if (state.currentScreen === 'game' && state.roomCode && (!isAsyncGame || leavingOwnLobby)) {
      if (!path.startsWith('/game')) {
        console.log('🎮 Game in progress - navigating to game screen');
        navigate(`/game/${state.roomCode}`, { replace: true });
      }
    } else if (state.currentScreen === 'lobby' && state.roomCode) {
      if (!path.startsWith('/room/') && !path.startsWith('/game')) {
        console.log('🏠 In lobby - navigating to room');
        navigate(`/room/${state.roomCode}`, { replace: true });
      }
    } else if (state.currentScreen === 'results') {
      // `/tables` is on this allow-list because of "Keep this table?": the card
      // lives on the results screen, and tapping through to the table you just
      // kept is the entire payoff. Without this the guard bounced you straight
      // back to the results you were trying to leave, and the table was
      // unreachable from the one place that offers it.
      if (!path.startsWith('/game-results') && !path.startsWith('/game') && !path.startsWith('/tables')) {
        console.log('📊 Results - navigating to game results');
        navigate('/game-results', { replace: true });
      }
    }
  }, [state.currentScreen, state.roomCode, location.pathname, navigate]);

  // Show loading while Firebase auth is initializing
  if (loading) {
    return (
      <div className="app-shell wr-center" style={{ minHeight: '100vh' }}>
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-gray-600">Initializing authentication...</p>
        </div>
      </div>
    );
  }

  return (
    // Hard cap the document at 100dvh + overflow:hidden so the global
    // wrapper never scrolls. Only the inner .screen-pad scrolls. Without
    // this, the bottom nav (position:fixed) docked correctly but the
    // top nav (position:sticky) lost its anchor when the document body
    // scrolled, so users on long screens watched it drift up. The mobile
    // browser chrome collapse-bar would also kick in on scroll, looking
    // janky.
    <div className="flex flex-col" style={{ background: 'var(--bg)', height: '100dvh', overflow: 'hidden' }}>
      {/* ONE version poller. There used to be two (VersionNotification +
          useVersionCheck), both hammering /api/health on a loop, so the page
          never reached network-idle and phones burned battery doing it.
          VersionNotification is deleted; UpdateNotification owns the check. */}
      <UpdateNotification />

      {/* Global Components are intentionally minimal in the redesigned UI.
          Atom now lives inside individual game screens via AtomBubble; the
          floating Atom mascot, FeedbackButton, and AchievementTracker are
          kept off the main canvas to preserve the calm/focus of the design. */}
      
      {/* Every route renders inside the boundary. Before this, a single
          render throw anywhere in the tree blanked the entire app — no
          message, no way back. Keyed on pathname so navigating away from a
          crashed screen resets the boundary instead of pinning the user to
          the error card forever. */}
      <ErrorBoundary boundary="routes" key={`eb-${location.pathname}`}>
      <Suspense fallback={
        <div className="app-shell">
          <div className="screen-pad">
            <div className="wr-col gap-2 mt-4">
              <SkeletonList count={5} rowHeight={64} />
            </div>
          </div>
        </div>
      }>
      <Routes location={location} key={location.pathname}>
        {/* Authentication routes */}
        <Route path="/login" element={<LoginScreen />} />
        <Route path="/signup" element={<SignUpScreen />} />
        <Route path="/forgot-password" element={<ForgotPasswordScreen />} />
        
        {/* Main app routes - protected by Firebase auth */}
        <Route path="/" element={<RootEntry />} />
        <Route path="/home" element={<ProtectedRoute><HomeScreenWrapper /></ProtectedRoute>} />
        <Route path="/game" element={<ProtectedRoute><GameScreen /></ProtectedRoute>} />
        <Route path="/results" element={<ProtectedRoute><ResultsScreen /></ProtectedRoute>} />
        <Route path="/game-results" element={<ProtectedRoute><GameResultsScreen /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><SettingsScreen /></ProtectedRoute>} />
        <Route path="/settings/blocked" element={<ProtectedRoute><BlockedPlayersScreen /></ProtectedRoute>} />
        <Route path="/admin/growth" element={<ProtectedRoute><AdminGrowthScreen /></ProtectedRoute>} />
        <Route path="/solo" element={<ProtectedRoute><SoloSetupScreen /></ProtectedRoute>} />
        {/* The shelf, the builder, and the confirmation screen. One route: a user
            walks that flow backwards as often as forwards, and three routes would
            make "change it" a browser-history problem instead of a state change. */}
        <Route path="/packs" element={<ProtectedRoute><PacksScreen /></ProtectedRoute>} />
        <Route path="/stats" element={<ProtectedRoute><StatsScreen /></ProtectedRoute>} />
        <Route path="/history" element={<ProtectedRoute><GameHistoryScreen /></ProtectedRoute>} />
        <Route path="/achievements" element={<ProtectedRoute><AchievementsScreen /></ProtectedRoute>} />
        <Route path="/plans" element={<ProtectedRoute><PlansScreen /></ProtectedRoute>} />
        <Route path="/how-it-works" element={<HowItWorksScreen />} />
        <Route path="/daily" element={<ProtectedRoute><DailyChallengeScreen /></ProtectedRoute>} />
        <Route path="/friends" element={<FriendsScreen />} />
        <Route path="/friends/:uid" element={<FriendProfileScreen />} />
        <Route path="/leaderboard" element={<LeaderboardScreen />} />
        {/* Tables are members-only — the server gates every read. */}
        <Route path="/tables" element={<ProtectedRoute><TablesScreen /></ProtectedRoute>} />
        <Route path="/tables/:tableId" element={<ProtectedRoute><TableScreen /></ProtectedRoute>} />
        <Route path="/u/:uid" element={<PublicProfileScreen />} />
        <Route path="/tv" element={<TVCanvasScreen />} />
        <Route path="/tv/:roomCode" element={<TVCanvasScreen />} />
        {/* Public completed-game viewer — works for guests; no auth gate. */}
        <Route path="/games/:gameId" element={<PublicGameResultsScreen />} />
        <Route path="/error" element={<EdgeStateScreen />} />
        <Route path="/debug" element={<ProtectedRoute><DebugScreen /></ProtectedRoute>} />
        
        {/* Room routes - PROTECTED */}
        <Route 
          path="/lobby" 
          element={<ProtectedRoute><LobbyRedirectHandler /></ProtectedRoute>} 
        />
        <Route 
          path="/room/:roomCode" 
          element={<ProtectedRoute><LobbyScreenWrapper /></ProtectedRoute>} 
        />
        
        {/* Direct join route - PROTECTED */}
        <Route 
          path="/join/:roomCode"
          // No ProtectedRoute — DirectJoinHandler auto-creates a guest
          // account if none exists so share links work for everyone.
          element={<DirectJoinHandler />} 
        />
        
        {/* Game routes - PROTECTED */}
        <Route 
          path="/game/:roomCode" 
          element={<ProtectedRoute><GameScreen /></ProtectedRoute>} 
        />
        
        {/* Results routes - PROTECTED */}
        <Route 
          path="/game-results/:roomCode" 
          element={<ProtectedRoute><GameScreen /></ProtectedRoute>} 
        />
        
        {/* Profile routes - PROTECTED */}
        <Route 
          path="/profile" 
          element={<ProtectedRoute><ProfileScreen /></ProtectedRoute>} 
        />
        <Route 
          path="/setup" 
          element={<ProtectedRoute><ProfileScreen /></ProtectedRoute>} 
        />
        <Route 
          path="/profile/new" 
          element={<ProtectedRoute><ProfileScreen /></ProtectedRoute>}
        />
        
        <Route 
          path="/profile/edit/:username" 
          element={<ProtectedRoute><ProfileScreen /></ProtectedRoute>} 
        />
        <Route 
          path="/profile/:username" 
          element={<ProtectedRoute><ProfileScreen /></ProtectedRoute>} 
        />
        
        {/* Catch all route - redirect to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
      </ErrorBoundary>
      {/* Persistent return-to-game pill — appears only when a game is live
          and the user has browsed away from the gameplay surface. */}
      <ActiveGamePill />
      {/* Global game-invite notifications — pops up top-right when a friend
          sends an invite. Mounted globally so the toast survives route
          changes. Auth-gated by the component itself. */}
      <IncomingInviteToast />
      {/* Global nav — a docked bottom bar below 1024px, a 72px icon rail
          above it. Hides itself during gameplay and auth flows on BOTH
          surfaces (a rail beside a live round is an invitation to abandon
          your table). */}
      <AppNav />
      {/* In-game nav — renders only on /game/*, /room/*, /game-results.
          Mutually exclusive with AppNav (which hides on those routes). */}
      <ContextualGameNav />
    </div>
  );
}

/**
 * `/` — two entirely different jobs behind one URL.
 *
 * SIGNED IN → the splash, exactly as before: a brief branded hold while
 * auth settles, then straight through to /home.
 *
 * SIGNED OUT → the LANDING. Not a splash-then-login-wall. The old logged-out
 * path burned 1.8 seconds on a logo and then showed a stranger a 480px sign-in
 * form — on what is, for most visitors, the only page of WordRush they will
 * ever see. The landing shows them the actual wheel, lets them spin it, and
 * offers a table with no account at all.
 */
function RootEntry() {
  const { currentUser, loading } = useAuth();
  if (loading) return <SplashScreen />;
  if (!currentUser) return <LandingScreen />;
  return <SplashScreen />;
}

/** Render the in-game nav only when on a gameplay route. Cheap render
 *  guard kept here rather than inside InGameNav so the component itself
 *  doesn't need to know about routing. */
function ContextualGameNav() {
  const location = useLocation();
  const inGameRoute =
    location.pathname.startsWith('/game') ||
    location.pathname.startsWith('/room/');
  if (!inGameRoute) return null;
  return <InGameNav />;
}

import { ThemeProvider } from './contexts/ThemeContext';

function App() {
  return (
    <ThemeProvider>
      <GameProvider>
        <AuthProvider>
          <AccessibilityProvider>
            <Router>
              <WebSocketProvider>
                <ToastProvider>
                  <ProfilePreviewProvider>
                    <AppContent />
                  </ProfilePreviewProvider>
                </ToastProvider>
              </WebSocketProvider>
            </Router>
          </AccessibilityProvider>
        </AuthProvider>
      </GameProvider>
    </ThemeProvider>
  );
}

export default App; 